import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { ArrowLeft, Lock, User, Loader2 } from "lucide-react"
import { motion } from "framer-motion"
import { useAuthStore } from "@/store/authStore"
import { useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"

export default function LoginPage() {
  const { toast } = useToast()
  const { login, init } = useAuthStore()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    init()

    // === ВРЕМЕННЫЙ СКРИПТ ДЛЯ СОЗДАНИЯ АДМИНА ===
    // Удали после первого успешного запуска!
    import('@/lib/createAdmin')
      .then(() => console.log("✅ Скрипт создания Админа запущен"))
      .catch(err => console.error("Ошибка запуска createAdmin:", err));
  }, [init])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const loginValue = (formData.get("login") as string)?.trim()
    const password = (formData.get("password") as string)?.trim()

    if (!loginValue || !password) {
      toast({
        title: "Ошибка",
        description: "Введите логин и пароль",
        variant: "destructive",
      })
      return
    }

    setIsLoading(true)

    const success = await login(loginValue, password)

    if (success) {
      toast({
        title: "Добро пожаловать!",
        description: `Вы вошли как ${loginValue}`,
      })
      navigate("/dashboard")
    } else {
      toast({
        title: "Ошибка входа",
        description: "Неверный логин или пароль",
        variant: "destructive",
      })
    }

    setIsLoading(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 overflow-hidden relative flex items-center justify-center p-6">
      {/* Фоновые декоративные свечения */}
      <div className="absolute inset-0 bg-[radial-gradient(at_50%_30%,rgba(168,85,247,0.08)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(at_20%_70%,rgba(129,140,248,0.06)_0%,transparent_60%)]" />

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, type: "spring", stiffness: 65, damping: 14 }}
        className="text-center max-w-5xl w-full relative z-10"
      >
        {/* Логотип */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 1, type: "spring" }}
          className="mb-12 flex justify-center"
        >
          <img
            src="/images/logo.png"
            alt="ОРВ ВСКС"
            className="w-[420px] h-auto drop-shadow-2xl filter brightness-110"
            onError={(e) => { e.currentTarget.style.display = "none" }}
          />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.9 }}
          className="text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tighter leading-none mb-6"
        >
          Отдел регионального
          <br />
          взаимодействия
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="text-2xl md:text-3xl text-purple-200/90 font-light tracking-wide mb-16"
        >
          Всероссийского студенческого корпуса спасателей
        </motion.p>

        {/* Форма входа */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="max-w-md mx-auto"
        >
          <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-2xl shadow-purple-950/60 p-10">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-2">
                <Label htmlFor="login" className="text-purple-200 text-lg font-medium">
                  Логин (фамилия)
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-3.5 h-6 w-6 text-purple-400" />
                  <Input
                    id="login"
                    name="login"
                    required
                    placeholder="Павлик"
                    disabled={isLoading}
                    className="pl-14 bg-white/10 border-white/30 text-white placeholder:text-purple-300/70 h-14 text-lg rounded-2xl focus:border-purple-400"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-purple-200 text-lg font-medium">
                  Пароль
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-3.5 h-6 w-6 text-purple-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    placeholder="••••••••"
                    disabled={isLoading}
                    className="pl-14 bg-white/10 border-white/30 text-white placeholder:text-purple-300/70 h-14 text-lg rounded-2xl focus:border-purple-400"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className={cn(
                  "w-full bg-white/10 hover:bg-white/15 backdrop-blur-xl border border-white/30 hover:border-white/50",
                  "text-white font-semibold text-2xl py-8 rounded-3xl shadow-lg shadow-purple-950/50 transition-all",
                  "disabled:opacity-70"
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-3 h-6 w-6 animate-spin" />
                    Вход...
                  </>
                ) : (
                  "Войти в систему"
                )}
              </Button>
            </form>

            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              disabled={isLoading}
              className="mt-8 text-purple-300 hover:text-white w-full text-lg py-6 rounded-2xl hover:bg-white/5"
            >
              <ArrowLeft className="mr-3 h-6 w-6" />
              Назад
            </Button>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="mt-16 text-purple-400/60 text-sm tracking-widest"
        >
          © 2025 ОРВ ВСКС • Закрытая корпоративная система
        </motion.p>
      </motion.div>

      {/* Декоративные блики */}
      <div className="absolute top-16 left-16 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[140px]" />
      <div className="absolute bottom-12 right-12 w-[700px] h-[700px] bg-indigo-500/10 rounded-full blur-[160px]" />
    </div>
  )
}